import { useEffect, useState } from "react";
import { X, DownloadSimple, Calendar, ChartBar, ClipboardText, Truck, Scales, Path, Wrench, Crosshair } from "@phosphor-icons/react";
import { companyApi, reportsApi } from "@/lib/api";

const SECTIONS = [
  { key: "include_summary", label: "Daily Summary", desc: "Job counts, weight moved, active assets, alerts", icon: ChartBar, accent: "#22c55e" },
  { key: "include_jobs", label: "Jobs Table", desc: "Every scheduled job with status & weight", icon: ClipboardText, accent: "#ff8c00" },
  { key: "include_assets", label: "Fleet Roster", desc: "Asset list with driver, status, load", icon: Truck, accent: "#4682b4" },
  { key: "include_compliance", label: "Compliance Check", desc: "Overweight, alerts, bridge overrides used", icon: Scales, accent: "#ef4444" },
  { key: "include_routes", label: "Routes for the Day", desc: "Saved routes with mileage + savings", icon: Path, accent: "#a78bfa" },
  { key: "include_maintenance", label: "Maintenance Due", desc: "Assets overdue or due-soon for service", icon: Wrench, accent: "#fbbf24" },
  { key: "include_pod_appendix", label: "POD Appendix", desc: "Thumbnail grid of GPS-stamped delivery photos", icon: Crosshair, accent: "#22c55e" },
];

/**
 * Modal: pick a date + tick/untick which sections to include.
 * Initial state is loaded from the company's saved daily_report_prefs.
 */
export default function ReportBuilderDialog({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [sections, setSections] = useState(() => {
    const init = {};
    SECTIONS.forEach((s) => { init[s.key] = true; });
    return init;
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    companyApi.me().then((c) => {
      const prefs = c?.settings?.daily_report_prefs || {};
      setSections((prev) => {
        const next = { ...prev };
        Object.keys(prev).forEach((k) => {
          if (prefs[k] !== undefined) next[k] = !!prefs[k];
        });
        return next;
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const toggle = (k) => setSections((prev) => ({ ...prev, [k]: !prev[k] }));
  const setAll = (v) => setSections(SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: v }), {}));
  const anySelected = Object.values(sections).some(Boolean);

  const url = reportsApi.dailyPdfUrl(date, sections);

  return (
    <div className="fixed inset-0 bg-black/80 z-[1000] flex items-center justify-center p-4" onClick={onClose} data-testid="report-builder-dialog">
      <div className="bg-[#0d0d0d] border border-[#2a2a2a] w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#2a2a2a] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DownloadSimple size={18} className="text-[#ff8c00]" weight="bold" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Build Report</div>
              <h2 className="font-heading font-black text-lg tracking-tight">Daily Dispatch PDF</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-[#71717a] hover:text-white p-1" data-testid="report-builder-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-[#2a2a2a]">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] font-bold mb-2 flex items-center gap-1">
            <Calendar size={11} weight="bold" /> Report Date
          </div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-3 py-2 text-sm font-mono"
            data-testid="report-builder-date"
          />
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] font-bold">Sections to Include</div>
            <div className="flex gap-1 text-[10px] uppercase tracking-[0.2em] font-bold">
              <button onClick={() => setAll(true)} className="border border-[#2a2a2a] hover:border-[#ff8c00] hover:text-[#ff8c00] px-2 py-0.5" data-testid="report-builder-all">All</button>
              <button onClick={() => setAll(false)} className="border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] px-2 py-0.5" data-testid="report-builder-none">None</button>
            </div>
          </div>
          <div className="space-y-1.5">
            {SECTIONS.map((s) => {
              const checked = !!sections[s.key];
              return (
                <label key={s.key} className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${checked ? "border-[#ff8c00] bg-[#ff8c00]/5" : "border-[#2a2a2a] hover:bg-[#1a1a1a]"}`} data-testid={`report-builder-section-${s.key}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(s.key)}
                    className="accent-[#ff8c00] w-4 h-4"
                    data-testid={`report-builder-toggle-${s.key}`}
                  />
                  <s.icon size={16} style={{ color: s.accent }} weight="bold" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{s.label}</div>
                    <div className="text-[11px] text-[#a1a1aa]">{s.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
          {loaded && (
            <p className="text-[10px] text-[#71717a] mt-3 leading-relaxed">
              Defaults pulled from Settings → Notifications → Daily Report. To change permanently, save your picks there.
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#2a2a2a] flex justify-end gap-2">
          <button onClick={onClose} className="border border-[#2a2a2a] text-[#a1a1aa] hover:text-white px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold" data-testid="report-builder-cancel">
            Cancel
          </button>
          <a
            href={anySelected ? url : "#"}
            onClick={(e) => { if (!anySelected) e.preventDefault(); else onClose(); }}
            target="_blank"
            rel="noreferrer"
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2 ${anySelected ? "bg-[#ff8c00] hover:bg-[#ff8c00]/90 text-black" : "bg-[#2a2a2a] text-[#71717a] cursor-not-allowed"}`}
            data-testid="report-builder-download"
          >
            <DownloadSimple size={12} weight="bold" /> Download PDF
          </a>
        </div>
      </div>
    </div>
  );
}
